import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EntityDetailDrawer from '../../EntityDetailDrawer';
import {
    BenchmarkRequestError, BenchmarkRun, DataSource, benchmarkService
} from '../../../services/benchmarkService';
import { count } from '../BenchmarkVocabulary';
import { runSubjectsNoun, selectionLine } from '../investigation/investigationVocabulary';
import SourceToggle from './SourceToggle';
import SuppliedRunList from './SuppliedRunList';
import { useSuppliedRuns } from './useSuppliedRuns';
import { dsVar, ink } from '../../../theme/tokens';

interface RunPickerDrawerProps {
    open: boolean;
    onClose: () => void;
    /** The run on screen. */
    currentRun: BenchmarkRun | null;
    /** The workflow's stock backtest family. */
    stockFamily: string;
    /** Every family the workflow's supplied samples may be recorded under. */
    suppliedFamilies: string[];
    projectId?: string | null;
    /** The run chosen, and which kind it is so it can open on the right tab. */
    onSelect: (runKey: number, dataSource: DataSource) => void;
}

/**
 * Switch between the institution's own benchmark and a supplied sample.
 *
 * Docked beside the results rather than a separate page, so the reader keeps
 * the run they are looking at in view while choosing another.
 */
const RunPickerDrawer: React.FC<RunPickerDrawerProps> = ({
    open, onClose, currentRun, stockFamily, suppliedFamilies, projectId, onSelect
}) => {
    const [source, setSource] = useState<DataSource>(currentRun?.dataSource === 'user_supplied' ? 'user_supplied' : 'mined');
    const [stockRun, setStockRun] = useState<BenchmarkRun | null>(null);
    const [stockLoading, setStockLoading] = useState(false);
    const [stockError, setStockError] = useState<string | null>(null);
    const [mineOnly, setMineOnly] = useState(false);

    // Open on the list the reader came from.
    useEffect(() => {
        if (open) setSource(currentRun?.dataSource === 'user_supplied' ? 'user_supplied' : 'mined');
    }, [open, currentRun?.dataSource]);

    const supplied = useSuppliedRuns({
        families: suppliedFamilies,
        projectId,
        mineOnly,
        enabled: open && source === 'user_supplied'
    });

    useEffect(() => {
        if (!open || source !== 'mined' || !stockFamily) return;
        let cancelled = false;
        setStockLoading(true);
        setStockError(null);
        benchmarkService.getCurrentRun({ flowFamily: stockFamily, projectId: projectId || undefined })
            .then((run) => { if (!cancelled) setStockRun(run); })
            .catch((err) => {
                if (!cancelled) setStockError(err instanceof BenchmarkRequestError ? err.message : 'Could not load the current benchmark.');
            })
            .finally(() => { if (!cancelled) setStockLoading(false); });
        return () => { cancelled = true; };
    }, [open, source, stockFamily, projectId]);

    const choose = (runKey: number, dataSource: DataSource) => {
        if (runKey !== currentRun?.runKey) onSelect(runKey, dataSource);
        onClose();
    };

    return (
        <EntityDetailDrawer
            open={open}
            onClose={onClose}
            docked
            dockedMinWidth={1280}
            icon={<SwapHorizIcon />}
            title="Switch run"
            description="Results from your institution's own alert history, or from a sample of alerts someone supplied. The two score different populations and are never compared directly."
            banner={
                <Box sx={{ mb: 2 }}>
                    <SourceToggle value={source} onChange={setSource} fullWidth />
                </Box>
            }
        >
            <Box sx={{ mt: 2.5 }}>
                {source === 'mined' ? (
                    stockLoading ? (
                        <Box className="flex items-center gap-2" sx={{ py: 3, justifyContent: 'center' }}>
                            <CircularProgress size={18} sx={{ color: dsVar('accent-fg') }} />
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>Loading the current benchmark…</Typography>
                        </Box>
                    ) : stockError ? (
                        <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5') }}>{stockError}</Typography>
                    ) : !stockRun ? (
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                            This workflow has not been benchmarked against your institution's history yet.
                        </Typography>
                    ) : (
                        <Box sx={{ p: 2, borderRadius: '8px', border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page', 0.5) }}>
                            <Box className="flex items-baseline justify-between gap-2">
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg-body') }}>
                                    Current benchmark
                                </Typography>
                                <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>Run #{stockRun.runKey}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.5 }}>
                                {count(stockRun.predictionCount)} {runSubjectsNoun(stockRun)} from your history
                                {stockRun.startedAt && ` · ${new Date(stockRun.startedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}`}
                                {selectionLine(stockRun.agentSelection) && ` · ${selectionLine(stockRun.agentSelection)}`}
                            </Typography>
                            <Button
                                fullWidth
                                onClick={() => choose(stockRun.runKey, 'mined')}
                                disabled={stockRun.runKey === currentRun?.runKey}
                                sx={{
                                    mt: 1.5, textTransform: 'none', fontWeight: 600, color: dsVar('on-accent'), backgroundColor: dsVar('accent'),
                                    '&:hover': { backgroundColor: '#F5D149' },
                                    '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') }
                                }}
                            >
                                {stockRun.runKey === currentRun?.runKey ? 'On screen' : 'Open this run'}
                            </Button>
                        </Box>
                    )
                ) : (
                    <SuppliedRunList
                        runs={supplied.runs}
                        loading={supplied.loading}
                        error={supplied.error}
                        onRetry={supplied.reload}
                        onSelect={(run) => choose(run.runKey, 'user_supplied')}
                        selectedRunKey={currentRun?.runKey ?? null}
                        mineOnly={mineOnly}
                        onMineOnlyChange={setMineOnly}
                    />
                )}
            </Box>
        </EntityDetailDrawer>
    );
};

export default RunPickerDrawer;
